/**
 * Quotation cart = quotation_request from backend (single source of truth).
 * Anonymous: request_id in cookie. Logged in: request by lead_id.
 * Panel cliente: historial de cotizaciones; repetir orden (futuro).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getWorkerApi, getWorkerApiWithAuth, postWorkerApi } from '@/lib/worker-api';
import {
  getRequestIdFromCookie,
  setRequestIdCookie,
  getOrCreateAnonymousId,
  clearQuotationRequestCookies,
} from '@/lib/quotation-request-cookies';
import { useSourcingItems, useBrands, useOrigins, useMoqUnits } from '@/hooks/use-store';
import type { QuotationRequestItem, QuotationDocBlocks } from '@/lib/store-types';

export interface QuotationCartLine {
  sourcingItemId: string;
  slug: string;
  name: string;
  moqValue: number;
  moqUnitId: string;
  moqUnitLabel: string;
  quantity: number;
  salesFormat?: string;
  imageUrl?: string;
  brandName?: string;
  originName?: string;
  unitsPerBox?: number;
  boxesPerPallet?: number;
  boxesPerContainer?: number;
}

const QUERY_KEY = ['quotation-request-cart'] as const;

/** Item shape from API/DB for conversion to cart line (includes optional moq_value). */
export type ApiItem = QuotationRequestItem & { moq_value?: number };

/** Convert a single request item to cart line for display in QuotationBlocksView. */
export function toCartLine(
  it: ApiItem,
  allItems: { id: string; slug: string; internal_name: string; sales_format?: string; packshot_image?: string; brand_id?: string; origin_id?: string; units_per_box?: number; boxes_per_pallet?: number; boxes_per_container?: number }[],
  brands: { id: string; name: string }[],
  origins: { id: string; name: string }[],
  moqUnits: { id: string; name: string }[]
): QuotationCartLine {
  const item = allItems?.find((a) => a.id === it.sourcing_item_id);
  const raw = it as ApiItem;
  const moqValue = typeof raw.moq_value === 'number' && raw.moq_value >= 0 ? raw.moq_value : 1;
  const moqUnitLabelRaw = it.moq_unit != null ? String(it.moq_unit) : '';
  const moqUnit = moqUnits?.find((m) => m.name === moqUnitLabelRaw) ?? moqUnits?.find((m) => m.name?.toLowerCase() === moqUnitLabelRaw?.toLowerCase()) ?? moqUnits?.[0];
  return {
    sourcingItemId: it.sourcing_item_id,
    slug: (it.slug ?? item?.slug ?? '') as string,
    name: (it.name ?? item?.internal_name ?? '') as string,
    moqValue,
    moqUnitId: (moqUnit?.id ?? '') as string,
    moqUnitLabel: (moqUnitLabelRaw || (moqUnit?.name ?? '')) as string,
    quantity: it.quantity ?? 0,
    salesFormat: item?.sales_format,
    imageUrl: item?.packshot_image,
    brandName: item?.brand_id ? brands?.find((b) => b.id === item.brand_id)?.name : undefined,
    originName: item?.origin_id ? origins?.find((o) => o.id === item.origin_id)?.name : undefined,
    unitsPerBox: item?.units_per_box,
    boxesPerPallet: item?.boxes_per_pallet,
    boxesPerContainer: item?.boxes_per_container,
  };
}

/** Lead as returned by GET /api/quotation-request when request has lead_id (for Destinatario). */
export interface QuotationRequestLead {
  id: string;
  email?: string;
  name?: string;
  phone?: string;
  company?: string;
  city?: string;
  country?: string;
  whatsapp?: string;
}

/** Bank account as returned by GET when request has bank_account_id (for "Nuestros datos bancarios" card). */
export interface QuotationRequestBankAccount {
  id: string;
  label?: string | null;
  bank_name?: string | null;
  account_holder?: string | null;
  account_number?: string | null;
  iban?: string | null;
  swift?: string | null;
  currency?: string | null;
  details?: string | null;
}

/** Document row as returned by GET /api/quotation-request (documents array). */
export interface QuotationRequestDocumentSnapshot {
  id: string;
  quotation_request_id: string;
  document_type: string;
  file_url: string;
  file_name?: string | null;
  uploaded_by_admin: boolean;
  created_at: string;
}

/** Request as returned by GET /api/quotation-request (select=*; includes blocks, document display, bank_account, documents). */
export interface QuotationRequestSnapshot {
  id: string;
  items: ApiItem[];
  blocks?: QuotationDocBlocks;
  /** Filled on submit; used to prefill form when editing a pending-turned-draft. */
  destination_port_or_address?: string | null;
  specifications?: string | null;
  incoterm?: string | null;
  /** Pre-accept and post-accept statuses (see store-types QuotationRequestStatus). */
  status?: QuotationRequestSnapshotStatus;
  /** Set when lead responds to cotizado. */
  outcome?: 'accepted' | 'rejected' | null;
  /** Document-level fields (addressee, payment, observaciones). */
  addressee_company?: string | null;
  addressee_contact?: string | null;
  final_price_text?: string | null;
  payment_type?: string | null;
  payment_method?: string | null;
  observaciones?: string | null;
  /** Document display for digital offer (preview cotizado / acceptance view): title, header, seal, final message. */
  document_title?: string | null;
  header_image_url?: string | null;
  seal_image_url?: string | null;
  final_message?: string | null;
  show_final_message?: boolean | null;
  /** Lead data when request has lead_id (for Destinatario). */
  lead?: QuotationRequestLead | null;
  /** Bank account when request has bank_account_id (for "Nuestros datos bancarios" card). */
  bank_account_id?: string | null;
  bank_account?: QuotationRequestBankAccount | null;
  etd_origin_port?: string | null;
  eta_destination_port?: string | null;
  /** Documents for this request (PDFs, receipts, shipping docs). */
  documents?: QuotationRequestDocumentSnapshot[] | null;
}

/** All possible status values returned by API (mirrors QuotationRequestStatus). */
export type QuotationRequestSnapshotStatus =
  | 'draft'
  | 'pending'
  | 'en_revision'
  | 'cotizado'
  | 'finished'
  | 'pending_deposit_payment'
  | 'in_process'
  | 'shipped'
  | 'pending_final_payment'
  | 'arriving_soon'
  | 'delivered';

/** True when the client cannot edit the cart (admin in review or quoted). */
export function isQuotationRequestLockedForClient(status: QuotationRequestSnapshotStatus | undefined): boolean {
  return status === 'en_revision' || status === 'cotizado';
}

const QuotationCartContext = createContext<{
  items: QuotationCartLine[];
  request: QuotationRequestSnapshot | null;
  requestId: string | null;
  add: (line: QuotationCartLine, options?: { mixBlockIndex?: number; forceNewContainer?: boolean }) => Promise<void>;
  updateQuantity: (sourcingItemId: string, quantity: number) => Promise<void>;
  remove: (sourcingItemId: string) => Promise<void>;
  patchBlocks: (blocks: QuotationDocBlocks) => Promise<void>;
  /** Set current request id (cookie + state) so cart loads this draft; use after "Continuar editando" or "Nueva cotización". */
  setCurrentRequestId: (id: string) => void;
  totalCount: number;
  isLoading: boolean;
  /** When true, client cannot edit (status en_revision or cotizado). */
  readOnly: boolean;
} | null>(null);

export function QuotationCartProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [anonRequestId, setAnonRequestId] = useState<string | null>(() => getRequestIdFromCookie());
  /** Sync override so "Continuar editando" / "Editar y reenviar" refetch uses the new id before state updates. */
  const pendingRequestIdRef = useRef<string | null>(null);

  const [sessionUser, setSessionUser] = useState<{ id: string } | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSessionUser(data?.session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_ev, session) => setSessionUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  // Invalidate store client data (lead, quotation_requests) when auth changes so sidebar and history update on login/logout.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['store', 'client'] });
  }, [sessionUser?.id, queryClient]);

  const { data: leads } = useQuery({
    queryKey: ['leads-by-auth', sessionUser?.id],
    queryFn: async () => {
      if (!sessionUser?.id) return [];
      const { data } = await supabase.from('leads').select('id').eq('auth_user_id', sessionUser.id).limit(1);
      return data ?? [];
    },
    enabled: !!sessionUser?.id,
  });
  const leadId = leads?.[0]?.id ?? null;

  const { data: allItems } = useSourcingItems(null);
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: moqUnits } = useMoqUnits();

  // Auth getter for worker API calls that require Authorization when the request has lead_id.
  const getSession = useCallback(() => supabase.auth.getSession(), []);

  const fetchRequest = useCallback(async (): Promise<QuotationRequestSnapshot | null> => {
    // Prefer ref so refetch after setCurrentRequestId(id) uses new id immediately (avoids empty cart on "Editar y reenviar").
    const effectiveAnonId = pendingRequestIdRef.current ?? anonRequestId;
    if (effectiveAnonId) {
      // Logged-in users need auth: GET /api/quotation-request returns 401 when the request has lead_id and no Authorization header.
      const path = `/api/quotation-request?id=${encodeURIComponent(effectiveAnonId)}`;
      const res = sessionUser
        ? await getWorkerApiWithAuth<QuotationRequestSnapshot>(path, getSession)
        : await getWorkerApi<QuotationRequestSnapshot>(path);
      return res.ok && res.data ? res.data : null;
    }
    if (leadId) {
      const res = await getWorkerApiWithAuth<QuotationRequestSnapshot>(
        `/api/quotation-request?lead_id=${encodeURIComponent(leadId)}`,
        getSession
      );
      return res.ok && res.data ? res.data : null;
    }
    return null;
  }, [leadId, anonRequestId, sessionUser, getSession]);

  const { data: request, isLoading } = useQuery({
    queryKey: [...QUERY_KEY, leadId ?? 'anon', anonRequestId ?? 'none'],
    queryFn: fetchRequest,
    staleTime: 30_000,
  });

  const setCurrentRequestId = useCallback((id: string) => {
    setRequestIdCookie(id);
    pendingRequestIdRef.current = id;
    setAnonRequestId(id);
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }, [queryClient]);

  useEffect(() => {
    if (leadId && request?.id && !getRequestIdFromCookie()) {
      setRequestIdCookie(request.id);
      setAnonRequestId(request.id);
    }
  }, [leadId, request?.id]);

  // Clear pending ref once state has caught up so we don't keep overriding.
  useEffect(() => {
    if (anonRequestId === pendingRequestIdRef.current) {
      pendingRequestIdRef.current = null;
    }
  }, [anonRequestId]);

  const rawItems = Array.isArray(request?.items) ? request.items : [];
  const items: QuotationCartLine[] = rawItems
    .filter((it): it is ApiItem => it != null && typeof it === 'object' && typeof (it as ApiItem).sourcing_item_id === 'string')
    .map((it) =>
      toCartLine(it as ApiItem, allItems ?? [], brands ?? [], origins ?? [], moqUnits ?? [])
    );

  const ensureRequestId = useCallback(async (): Promise<string | null> => {
    if (leadId) {
      const res = await postWorkerApi<{ request_id: string }>('/api/quotation-request/ensure', { lead_id: leadId });
      return res.ok && res.data?.request_id ? res.data.request_id : null;
    }
    const fromCookie = getRequestIdFromCookie();
    const res = await postWorkerApi<{ request_id: string }>('/api/quotation-request/ensure', {
      request_id: fromCookie ?? undefined,
      anonymous_id: fromCookie ? undefined : getOrCreateAnonymousId(),
    });
    if (!res.ok || !res.data?.request_id) return null;
    const rid = res.data.request_id;
    setRequestIdCookie(rid);
    setAnonRequestId(rid);
    return rid;
  }, [leadId]);

  /** Create a new draft request (e.g. when current request is no longer draft and user adds a product for a new quotation). */
  const ensureNewRequestId = useCallback(async (): Promise<string | null> => {
    if (leadId) {
      const res = await postWorkerApi<{ request_id: string }>('/api/quotation-request/ensure', {
        lead_id: leadId,
        create_new: true,
      });
      if (!res.ok || !res.data?.request_id) return null;
      const rid = res.data.request_id;
      setRequestIdCookie(rid);
      setAnonRequestId(rid);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      return rid;
    }
    clearQuotationRequestCookies();
    setAnonRequestId(null);
    const res = await postWorkerApi<{ request_id: string }>('/api/quotation-request/ensure', {
      anonymous_id: getOrCreateAnonymousId(),
    });
    if (!res.ok || !res.data?.request_id) return null;
    const rid = res.data.request_id;
    setRequestIdCookie(rid);
    setAnonRequestId(rid);
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    return rid;
  }, [leadId, queryClient]);

  const add = useCallback(
    async (line: QuotationCartLine, options?: { mixBlockIndex?: number; forceNewContainer?: boolean }) => {
      const currentNotDraft = request?.status != null && request.status !== 'draft';
      const rid = currentNotDraft
        ? await ensureNewRequestId()
        : leadId
          ? (request?.id ?? (await ensureRequestId()))
          : (anonRequestId ?? (await ensureRequestId()));
      if (!rid) return;
      const body: Record<string, unknown> = {
        request_id: rid,
        sourcing_item_id: line.sourcingItemId,
        quantity: line.quantity,
        moq_unit: line.moqUnitLabel,
        moq_value: line.moqValue,
        name: line.name,
        slug: line.slug,
      };
      if (typeof options?.mixBlockIndex === 'number' && Number.isInteger(options.mixBlockIndex)) {
        body.mix_block_index = options.mixBlockIndex;
      }
      if (options?.forceNewContainer === true) {
        body.force_new_container = true;
      }
      await postWorkerApi('/api/quotation-request/add-item', body);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    [leadId, request?.id, request?.status, anonRequestId, ensureRequestId, ensureNewRequestId, queryClient]
  );

  const updateQuantity = useCallback(
    async (sourcingItemId: string, quantity: number) => {
      const rid = request?.id ?? anonRequestId;
      if (!rid) return;
      await postWorkerApi('/api/quotation-request/update-quantity', {
        request_id: rid,
        sourcing_item_id: sourcingItemId,
        quantity,
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    [request?.id, anonRequestId, queryClient]
  );

  const remove = useCallback(
    async (sourcingItemId: string) => {
      const rid = request?.id ?? anonRequestId;
      if (!rid) return;
      await postWorkerApi('/api/quotation-request/remove-item', {
        request_id: rid,
        sourcing_item_id: sourcingItemId,
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    [request?.id, anonRequestId, queryClient]
  );

  const patchBlocks = useCallback(
    async (blocks: QuotationDocBlocks) => {
      const rid = request?.id ?? anonRequestId;
      if (!rid) return;
      await postWorkerApi('/api/quotation-request/patch-blocks', { request_id: rid, blocks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    [request?.id, anonRequestId, queryClient]
  );

  const requestId = request?.id ?? anonRequestId;

  useEffect(() => {
    const clear = () => {
      clearQuotationRequestCookies();
      setAnonRequestId(null);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    };
    window.addEventListener('quotation-cart-cleared', clear);
    return () => window.removeEventListener('quotation-cart-cleared', clear);
  }, [queryClient]);

  const totalCount = items.reduce((acc, i) => acc + i.quantity, 0);
  const readOnly = isQuotationRequestLockedForClient(request?.status);

  /** Cuando la cotización está pending o en_revision, el carrito se considera vacío en UI: no número en sidebar ni indicaciones de la cotización anterior (solo en cotizado/finished se muestra la vista de aceptación con ítems). */
  const isCartEmptyForDisplay =
    request?.status === 'pending' || request?.status === 'en_revision';
  const displayItems = isCartEmptyForDisplay ? [] : items;
  const displayTotalCount = isCartEmptyForDisplay ? 0 : totalCount;

  return (
    <QuotationCartContext.Provider
      value={{
        items: displayItems,
        request,
        requestId,
        add,
        updateQuantity,
        remove,
        patchBlocks,
        setCurrentRequestId,
        totalCount: displayTotalCount,
        isLoading,
        readOnly,
      }}
    >
      {children}
    </QuotationCartContext.Provider>
  );
}

export function useQuotationCart() {
  const ctx = useContext(QuotationCartContext);
  if (!ctx) throw new Error('useQuotationCart must be used within QuotationCartProvider');
  return ctx;
}
